import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { api_routes, db_tables, routes } from '@app/consts';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { GoalPlan, GoalPlanSubmit } from './models/goal-plan.model';
import { GoalPlanService } from './services/goal-plan.service';
import { RowActionButtonInput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';

@Component({
  selector: 'app-goal-plans',
  templateUrl: './goal-plans.component.html',
  styleUrls: ['./goal-plans.component.scss']
})
export class GoalPlansComponent implements OnInit {
  public columns: any[] = [
    { field: "name", tableId: "tfi_PGpName", title: "Name" },
    { field: "description", tableId: "tfi_PGpDescription", title: "Description" },
    { field: "permitEmployeeGoalEntry", tableId: "tfi_PGpPermitEmployeeGoalEntry", title: this.translate.instant("GoalPlan-PermitEmployeeGoalEntry"), type: 'booleanToggle' },
    { field: "permitEmployeeChildGoalEntry", tableId: "tfi_PGpPermitEmployeeChildGoalEntry", title: this.translate.instant("GoalPlan-PermitEmployeeChildGoalEntry"), type: 'booleanToggle' },
    { field: "lockGoals", tableId: "tfi_PGpLockGoals", title: this.translate.instant("GoalPlan-LockGoals"), type: 'booleanToggle' },
    { field: "createdBy", tableId: "", title: this.translate.instant("GoalPlan-CreatedBy"), type: 'employee' },
    { field: "createdOn", tableId: "", title: this.translate.instant("GoalPlan-CreatedOn"), type: 'date' },
    { field: "lastPublishedOn", tableId: "", title: this.translate.instant("GoalPlan-LastPublishedOn"), type: 'dateTime' },
    { field: "status", subField: "name", tableId: "tfi_PGpStatus", title: "Status", type: 'chip', chipValue: ['Active', 'Archived'] },
  ];

  filterCategories: any[] = [
    { field: "name", tableId: "tfi_PGpName", title: "Name", type: "String", dataType: 'String'},
    { field: "description", tableId: "tfi_PGpDescription", title: "Description", type: "String", dataType: 'String'},
    { field: "lockGoals", tableId: "tfi_PGpLockGoals", title: this.translate.instant("GoalPlan-LockGoals"), dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
    { field: "permitEmployeeGoalEntry", tableId: "tfi_PGpPermitEmployeeGoalEntry", title: this.translate.instant("GoalPlan-PermitEmployeeGoalEntry"), dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
    { field: "permitEmployeeChildGoalEntry", tableId: "tfi_PGpPermitEmployeeChildGoalEntry", title: this.translate.instant("GoalPlan-PermitEmployeeGoalEntry"), dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
    { field: "createdBy", tableId: "", title: this.translate.instant("GoalPlan-CreatedBy"), type: "String", dataType: 'String', filterValue: ["CreatedBy.FirstName", "CreatedBy.LastName"]},
    { field: "createdOn", tableId: "", title: this.translate.instant("GoalPlan-CreatedOn"), type: "date", dataType: 'Date'},
    { field: "lastPublishedOn", tableId: "", title: this.translate.instant("GoalPlan-LastPublishedOn"), type: "date", dataType: 'Date'},
    { field: "status.name", subField: "name", tableId: "tfi_PGpStatus", title: "Status", dataType: "SpecialLookup", lookupCode: 'GOAL_TYPE_STATUS'},
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name"},
    { field: "description", sortValue: "description" },
    { field: "permitEmployeeGoalEntry", sortValue: "permitEmployeeGoalEntry" },
    { field: "permitEmployeeChildGoalEntry", sortValue: "permitEmployeeChildGoalEntry" },
    { field: "lockGoals", sortValue: "lockGoals" },
    { field: "createdBy", sortValue: ["Version.CreatedBy.FirstName", "Version.CreatedBy.LastName"] },
    { field: "createdOn", sortValue: "Version.CreatedOn" },
    { field: "lastPublishedOn", sortValue: "lastPublishedOn" },
    { field: "status", sortValue: "Status.Name" },
  ];

  gridActionButtons: RowActionButtonInput[] = [
    {
      id: 'copy',
      name: this.translate.instant('Copy'),
      icon: 'content_copy'
    }
  ];
  
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  public gridId: string = 'hubbubhr-goal-plans-grid';
  isLoading: boolean;
  pageSize: number = 10;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  searchValue: string;
  searchFilterString: string;
  getGoalsPlansRequest: any;
  sortString: string;
  filterString: string;

  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private overlayService: OverlayService,
    private goalPlanService: GoalPlanService
  ) { }

  ngOnInit(): void {
    this.getGoalPlans();
  }

  getGoalPlans() {
    this.isLoading = true;

    let filter;
    if(this.searchFilterString) {
      filter = this.searchFilterString;
    }
    if(this.filterString) {
      if(filter){
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }

    this.getGoalsPlansRequest = this.goalPlanService.getGoalPlans(this.pageSize, this.skip, this.sortString, filter)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
        }
      );
  }

  editOrCreatePlan(goalPlan?: GoalPlan) {
    if(goalPlan) {
      this.router.navigate([`${routes.PERFORMANCE}/${routes.GOAL_PLANS}/${routes.EDITOR}/${goalPlan.id}`]);
    }
    else {
      this.openFormDialog();
    }
  }

  openFormDialog() {
    let formData = {
      id: null,
      name: null,
      description: null
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('GoalPlan')}`,
      formId: 'frm_1pkiJ44rrnGYUP',
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.createNewGoalPlan(event)
    });
  }

  createNewGoalPlan(formData: GoalPlanSubmit){
    this.overlayService.show();

    this.goalPlanService.createGoalPlan(formData)
    .pipe(
        finalize(() => this.overlayService.hide())
    )
    .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(res);
          this.router.navigate([`${routes.PERFORMANCE}/${routes.GOAL_PLANS}/${routes.EDITOR}/${res.goalPlanId}`]);
        }
    );
  }

  deleteGoalPlans(goalPlanIds: string[]) {
    const observables = goalPlanIds.map(selectedItem => defer(() => this.goalPlanService.deleteGoalPlan(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.getGoalPlans();
        this.clearSelectedItems = !this.clearSelectedItems;
        this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      }
    );
  }
  
  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(name like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    if (this.getGoalsPlansRequest) {
      this.getGoalsPlansRequest.unsubscribe();
    }
    this.getGoalPlans();
  }

  rowActionEvent(event: any) {
    if (event.id === 'copy') {
      this.copyGoalPlan(event.rowData.id);
    }
  }

  copyGoalPlan(goalPlanId: string) {
    this.overlayService.show();
    this.goalPlanService.copyGoalPlan(goalPlanId)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CopiedSuccessfully')}`, 'clear', 'success');
          if(res.id && res.id !== null) {
            this.router.navigate([`${routes.PERFORMANCE}/${routes.GOAL_PLANS}/${routes.EDITOR}/${res.id}`]);
          }
        }
      );
  }
}
